import React from 'react';
import { Building2, Phone, Mail, CheckCircle2, Clock, ExternalLink, HelpCircle } from 'lucide-react';
import { AddressLink } from './MapsNavProvider';

// iter293 — Read-only display of the PM relationship for a job:
//   PM Company (parent org) → Service Location (property) → Requester (who called it in).
// Reads the persisted appointment requester SNAPSHOT (pm_requester_name/phone/email +
// requester_state) so historical jobs stay accurate even if the contact changes later.
// Requester is ALWAYS shown independently from Bill To. Renders nothing for non-PM jobs.
const REQ_STATE = {
  verified: { label: 'Verified contact', cls: 'text-green-400 border-green-700/40', Icon: CheckCircle2 },
  pending: { label: 'Unlisted / pending', cls: 'text-amber-400 border-amber-700/40', Icon: Clock },
  external: { label: 'External contact', cls: 'text-cyan-300 border-cyan-700/40', Icon: ExternalLink },
  unknown: { label: 'Unknown / not provided', cls: 'text-[#A3A3A3] border-[#525252]', Icon: HelpCircle },
};

export const PmJobContact = ({ job, testId = 'pm-job-contact' }) => {
  if (!job || !(job.pm_company_id || job.pm_company_name)) return null;
  const state = job.requester_state || '';
  const meta = REQ_STATE[state];
  const reqName = state === 'unknown'
    ? 'Requester Unknown / Not Provided'
    : (job.pm_requester_name || '—');
  const phone = job.pm_requester_phone || '';
  const email = job.pm_requester_email || '';
  return (
    <div data-testid={testId} className="bg-[#0A0A0A] border border-cyan-700/40 rounded-md p-3 space-y-3">
      <p className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold flex items-center gap-1">
        <Building2 className="w-3 h-3" /> Property Management Job
      </p>

      <div>
        <p className="text-[10px] text-[#737373] uppercase">PM Company</p>
        <p data-testid={`${testId}-company`} className="text-white text-sm">{job.pm_company_name || '—'}</p>
      </div>

      {(job.property_name || job.address) && (
        <div>
          <p className="text-[10px] text-[#737373] uppercase">Service Location</p>
          {job.property_name && (
            <p data-testid={`${testId}-property`} className="text-white text-sm">{job.property_name}</p>
          )}
          {job.address && (
            <AddressLink
              address={job.address}
              testId={`${testId}-address`}
              className="text-cyan-200 text-sm mt-0.5"
              iconClassName="w-3.5 h-3.5 flex-shrink-0"
            />
          )}
        </div>
      )}

      <div>
        <p className="text-[10px] text-[#737373] uppercase">Requested By / Job Contact</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {meta?.Icon && <meta.Icon className={`w-3.5 h-3.5 ${meta.cls.split(' ')[0]}`} />}
          <span data-testid={`${testId}-requester`} className="text-white text-sm">{reqName}</span>
          {meta && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>}
        </div>
        {phone && (
          <a
            href={`tel:${phone.replace(/[^+\d]/g, '')}`}
            data-testid={`${testId}-phone`}
            className="inline-flex items-center gap-2 mt-2 px-3 h-9 rounded-md bg-cyan-600 text-white font-bold hover:bg-cyan-500 active:bg-cyan-600 text-sm"
          >
            <Phone className="w-4 h-4" /> {phone}
            <span className="text-[10px] uppercase ml-1 opacity-80">Tap to Call</span>
          </a>
        )}
        {email && (
          <a
            href={`mailto:${email}`}
            data-testid={`${testId}-email`}
            className="text-cyan-200 text-sm hover:text-cyan-100 inline-flex items-center gap-1.5 break-all mt-1.5"
          >
            <Mail className="w-3.5 h-3.5 flex-shrink-0" /> {email}
          </a>
        )}
      </div>
    </div>
  );
};

export default PmJobContact;
